<template>
  <div>
    <template v-if="isValidUUID">
      <div v-if="loading" class="align-self-stretch text-center">
        <p class="mb-4 text-h6">Loading Application ...</p>
        <v-progress-linear color="secondary" indeterminate rounded height="8"></v-progress-linear>
      </div>
      <div v-else>
        <template v-if="appointment">
          <template v-if="!isPublic">
            <appointment-details-guest
              :appointment-warehouse="appointmentWarehouse"
              :appointment="appointment"
              :show-header="false">
              <template v-slot:footer-actions>
                <div class="mt-8 appointment-details-wrapper">
                  <appointment-action-group
                    :appointment-warehouse="appointmentWarehouse"
                    :appointment="appointment"
                    :appointment-actions="footerButtons">
                  </appointment-action-group>
                </div>
              </template>
            </appointment-details-guest>
          </template>

          <template v-else>
            <appointment-details-public :appointment="appointment"></appointment-details-public>
          </template>

          <template v-if="isPublic">
            <div class="d-flex flex-row justify-center align-center mt-3">
              <a
                class="btn primary-button mx-1"
                :href="`${config.helios_url}/appointments?appointmentId=${appointment.id}`"
                target="_blank"
                rel="noopener">
                Edit as Warehouse
              </a>
              <a
                class="btn primary-button mx-1"
                href="https://carrier.opendock.com"
                target="_blank"
                rel="noopener">
                Edit as Carrier
              </a>
            </div>
          </template>
        </template>
        <template v-else>
          <div class="d-flex justify-center">
            <v-alert type="error" class="text-center"
              >Either this appointment no longer exists or you do not have access to view it
            </v-alert>
          </div>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="d-flex justify-center">
        <v-alert type="error" class="text-center">Invalid appointment ID</v-alert>
      </div>
    </template>
  </div>
</template>

<script>
import appointmentMixin from '@/components/mixins/appointmentMixin';
import appointmentDetailsMixin from '@satellite/components/mixins/appointmentDetailsMixin';
import { isUUID } from 'class-validator';

export default {
  mixins: [appointmentMixin, appointmentDetailsMixin],
  props: {
    isPublic: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    isValidUUID() {
      return isUUID(this.appointmentId);
    },
    appointmentId() {
      return this.$route.params.appointmentId;
    },
    footerButtons() {
      return {
        reschedule: {
          icon: 'calendar-edit',
          label: 'Reschedule',
          action: 'openRescheduleDialog',
          tooltip: 'Reschedule Appointment',
          disabled: !this.canUpdate || !this.canReschedule,
          show: true
        },
        cancel: {
          icon: 'delete-outline',
          label: 'Cancel',
          action: 'confirmCancel',
          tooltip: 'Cancel Appointment',
          color: 'error',
          disabled: !this.canCancel,
          show: true
        }
      };
    }
  },
  data() {
    return {
      loading: true,
      appointment: null
    };
  },
  methods: {
    async getAppointmentById() {
      await this.$store
        .dispatch('Appointments/getAppointmentById', this.appointmentId)
        .then(response => {
          this.appointment = response;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async getPublicAppointmentById() {
      await axios
        .get(`appointment/public/${this.appointmentId}`)
        .then(response => {
          this.appointment = response?.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setUpdateActionDisplays() {
      if (!this.canReschedule) {
        this.footerButtons.reschedule.disabled = true;
        this.footerButtons.reschedule.tooltip =
          'Contact the warehouse to reschedule your appointment';
      }
      if (!this.canUpdate) {
        this.footerButtons.reschedule.disabled = true;
        this.footerButtons.reschedule.tooltip =
          this.enforcedLeadTimeRules || 'Cannot reschedule appointment';
      }
      if (!this.canCancel) {
        this.footerButtons.cancel.disabled = true;
        this.footerButtons.cancel.tooltip =
          this.enforcedLeadTimeRules || 'Cannot cancel appointment';
      }
      if (this.novaCore.isRequested(this.appointment)) {
        this.footerButtons.cancel.label = 'Cancel Request';
      }
    },
    async appointmentUpdateHandler() {
      if (this.appointmentId) {
        this.isPublic ? await this.getPublicAppointmentById() : await this.getAppointmentById();
      }
    }
  },
  async mounted() {
    this.isPublic ? await this.getPublicAppointmentById() : await this.getAppointmentById();
    this.setUpdateActionDisplays();
    this.$eventHub.$on(['appointment-updated'], this.appointmentUpdateHandler);
  },
  beforeDestroy() {
    this.$eventHub.$off(['appointment-updated'], this.appointmentUpdateHandler);
  },
  watch: {
    appointment() {
      this.setUpdateActionDisplays();
    }
  }
};
</script>
